/**
 * D420: the arena theme's art, once for both renderers, as the compute theme's is
 * (`iso-kit-compute-art.ts`, whose solids and units this builds on). A stronghold grows from a
 * camp to a fortress: campfires and tents first, then lumber camps, quarries and watchtowers, on
 * to forges, stables, shrines, great halls, wizard towers and dragon roosts. Every producer's
 * plot, the yard's keep and vault, each chain's building, each tournament's mark and the
 * stronghold's own wall and gate are described here as `ArenaUnit`s: a footprint and the simple
 * solids on it. To the compute theme's boxes, cylinders, rods, panels, discs and balls the arena
 * adds cones (tents, tower roofs, spires), gabled roofs, flames, crystals and flags. The flat map
 * (`iso-kit-arena.ts`) paints them in SVG; the 3D map (`idle/three/arena.ts`) builds them as
 * instanced meshes. Heights are in world units (a tile is one unit across).
 *
 * D350: a plot grows wide with its count (the shared grid fills its tiles) and tall with its
 * milestones (`tier`): fires grow to bonfires, tents to pavilions, lookouts to stone towers,
 * halls take floors. Windows light at dusk and night; flames and crystals glow at every hour.
 * Original designs only (D9, D420): nothing from any game's heroes, items, maps or factions.
 */
import type { IsoBuildingAt } from './IsoWorldMap.tsx';
import { heartForm, shade, tint, type Bounds, type IsoParcel } from './iso.ts';
import {
  hashXY,
  mainRect,
  pieceTop as basePieceTop,
  type FaceMarks,
  type Look,
  type Piece,
  type Unit,
} from './iso-kit-compute-art.ts';

/* ---------- the pieces ---------- */

/** A cone: a tent's roof, a tower's, a spire. `sides` 4 makes it a pyramid. */
export interface ConePiece {
  s: 'cone';
  x: number;
  y: number;
  r: number;
  z: number;
  h: number;
  c: string;
  sides?: 4 | 6;
}
/** A gabled roof over x..x+w, y..y+d, its ridge `h` over `z`, along x when `along`. */
export interface GablePiece {
  s: 'gable';
  x: number;
  y: number;
  w: number;
  d: number;
  z: number;
  h: number;
  c: string;
  along: boolean;
  /** The gable ends' colour, the walls' under it; the roof's, darker, when left out. */
  end?: string;
}
/** A flame, always lit: a torch, a campfire, a brazier, a forge's hearth. */
export interface FlamePiece {
  s: 'flame';
  x: number;
  y: number;
  z: number;
  r: number;
  h: number;
}
/** A crystal, always glowing: a mine's, a wizard's orb, a spire's star. */
export interface CrystalPiece {
  s: 'crystal';
  x: number;
  y: number;
  z: number;
  r: number;
  h: number;
  c: string;
}
/** A flag hung from a pole's top at (x, y, z), flying `w` towards +x and `h` deep. */
export interface FlagPiece {
  s: 'flag';
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  c: string;
}

export type ArenaPiece = Piece | ConePiece | GablePiece | FlamePiece | CrystalPiece | FlagPiece;

/** A footprint and what stands on it, as the compute theme's `Unit`, with the arena's pieces. */
export type ArenaUnit = Omit<Unit, 'pieces' | 'fine'> & {
  pieces: ArenaPiece[];
  fine?: ArenaPiece[];
};

/* ---------- the theme's colours (original, D9) ---------- */

export const A = {
  earth: '#9C8463',
  packed: '#8F7A5A',
  sand: '#D6C08E',
  gravel: '#A8A196',
  flags: '#B9B2A3',
  rock: '#8E8A82',
  rockDark: '#6F6B64',
  timber: '#8A5E3B',
  timberDark: '#5E3F27',
  log: '#7A5434',
  logEnd: '#C9A273',
  plank: '#A77B4F',
  stone: '#B8B2A6',
  stoneDark: '#8E887C',
  stoneLight: '#D4CFC4',
  marble: '#E8E4DA',
  slate: '#5D6470',
  thatch: '#C9A35B',
  tile: '#A2483A',
  canvas: '#E8DFC8',
  canvasRed: '#B8463A',
  canvasBlue: '#3E6A9E',
  canvasGreen: '#4F7F4A',
  canvasGold: '#D8A93E',
  iron: '#4A4D52',
  steel: '#9AA0A6',
  gold: '#D9B24A',
  flame: '#FF9A3C',
  flameCore: '#FFE08A',
  ember: '#E0552B',
  crystal: '#6FE3FF',
  violet: '#B88CFF',
  spirit: '#9FF5E6',
  star: '#FFE7A0',
  water: '#4F86A8',
  moat: '#3E6A80',
  window: '#3A3430',
  lit: '#FFC870',
  straw: '#D9BE6A',
  red: '#B03A2E',
  white: '#EDE8DC',
  leaf: '#4F7A3E',
  pine: '#35603F',
  bone: '#E2D9C2',
  dragon: '#7C3B3B',
  dragonBelly: '#C98A5A',
  wing: '#A0524A',
  horse: ['#7A5232', '#3B302A', '#C9B79A', '#9A6A44'],
  skin: '#E3BC98',
  /** Life, 3D only: crows over the roofs, smoke off the fires. */
  crow: '#2E2C2E',
  crowWing: '#454247',
  smoke: '#B9B4AE',
} as const;

/** D291: the walls decoration, the keep's and the halls' walls. */
export const WALLS: Record<string, string> = {
  walls_timber: '#8A5E3B',
  walls_white: '#E6E0D2',
  walls_grey: '#A9A59C',
  walls_red: '#A0524A',
  walls_dark: '#4F4A45',
  walls_ivy: '#6F8A55',
  walls_marble: '#E8E4DA',
  walls_gilded: '#D2B05A',
};
/** D291: the roof decoration; `roof_as_built` keeps each building's own. */
export const ROOFS: Record<string, string> = {
  roof_thatch: '#C9A35B',
  roof_slate: '#5D6470',
  roof_red: '#A2483A',
  roof_copper: '#5E9A88',
};
/** D331: the path decoration lays the yard. */
export const PATHS: Record<string, string> = {
  path_dirt: '#9C8463',
  path_cobbles: '#9E978A',
  path_flagstones: '#B9B2A3',
  path_sand: '#D6C08E',
  path_mosaic: '#C9A07A',
  path_brick: '#A5583F',
  path_lanterns: '#A39B8C',
};
/** D331: the boundary decoration: the banners flown on the stronghold's wall and towers. */
export const BANNERS: Record<string, string> = {
  banners_red: '#B03A2E',
  banners_blue: '#2F5E9E',
  banners_green: '#3E7A46',
  banners_white: '#EDE8DC',
  banners_black: '#2A2A2E',
  banners_garland: '#D96A9A',
};
/** D331: the gate decoration: the gate's leaves. */
export const GATES: Record<string, string> = {
  gate_timber: '#7A5434',
  gate_iron: '#4A4D52',
  gate_portcullis: '#3A3D42',
  gate_carved: '#A0703E',
  gate_feast: '#C9463A',
};
/** D291: the vehicle decoration: the cart's paint. */
export const CARTS: Record<string, string> = {
  cart_red: '#A8402F',
  cart_green: '#3E7A46',
  cart_blue: '#2F5E9E',
  cart_yellow: '#D8A93E',
  cart_orange: '#C96A2E',
  cart_oak: '#8A5E3B',
  cart_tourney: '#7A3E8E',
};
/** D331: the worker decoration: the stewards' tunics. */
export const TUNICS: Record<string, string> = {
  tunic_plain: '#8C7A5E',
  tunic_wool: '#B8A88A',
  tunic_leather: '#7A5434',
  tunic_mail: '#8B9096',
  tunic_fur: '#6B5040',
  tunic_tabard: '#2F5E9E',
  tunic_gold: '#D9B24A',
};
/** D287: the track's surface: a dirt road, cobbles, then paving. */
export const ROADS: Record<string, string> = {
  dirt_road: '#8E7656',
  cobbled_road: '#8F8A80',
  paved_road: '#B5AFA2',
};
/** D380: who calls at the gate, by the data's visitor id: cloak, trim, and a horse if ridden. */
export const ARENA_VISITOR: Record<string, { coat: string; trim: string; horse?: string }> = {
  tax_collector: { coat: '#3E3A5E', trim: '#D9B24A' },
  messenger: { coat: '#2F5E9E', trim: '#EDE8DC', horse: '#7A5232' },
  pilgrim: { coat: '#9A8A6A', trim: '#5E4130' },
  royal_inspector: { coat: '#7A2E3E', trim: '#D9B24A', horse: '#E6E0D2' },
  minstrel: { coat: '#4F7F4A', trim: '#D96A9A' },
};
/** D380: a lucky find on the yard: a small chest, its gold glinting. */
export const ARENA_FIND = { chest: '#7A5434', band: '#4A4D52', gold: '#F2C94C' } as const;

/** How far winter whitens the stronghold's roofs, grass and hard ground. */
export const WINTER = { roof: 0.75, grass: 0.6, hard: 0.35 } as const;

/* ---------- small helpers ---------- */

const steps = (tier: number, per: number, cap: number): number =>
  Math.max(0, Math.min(cap, Math.floor(tier / per)));

function tilesOf(p: IsoParcel): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const k of p.tiles) {
    const [a, b] = k.split(',');
    const x = Number(a),
      y = Number(b);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push([x, y]);
  }
  return out;
}

type Box = Extract<Piece, { s: 'box' }>;

const box = (
  x: number,
  y: number,
  w: number,
  d: number,
  z: number,
  h: number,
  c: string,
  o: Partial<Box> = {},
): Box => ({ s: 'box', x, y, w, d, z, h, c, ...o });
const cyl = (
  x: number,
  y: number,
  r: number,
  z: number,
  h: number,
  c: string,
  top?: string,
): Piece => ({ s: 'cyl', x, y, r, z, h, c, ...(top ? { top } : {}) });
const rod = (
  a: [number, number, number],
  b: [number, number, number],
  t: number,
  c: string,
): Piece => ({ s: 'rod', a, b, t, c });
const cone = (
  x: number,
  y: number,
  r: number,
  z: number,
  h: number,
  c: string,
  sides: 4 | 6 = 6,
): ConePiece => ({ s: 'cone', x, y, r, z, h, c, sides });
const gable = (
  x: number,
  y: number,
  w: number,
  d: number,
  z: number,
  h: number,
  c: string,
  along = w >= d,
  end?: string,
): GablePiece => ({ s: 'gable', x, y, w, d, z, h, c, along, ...(end ? { end } : {}) });
const flame = (x: number, y: number, z: number, r: number, h = r * 2.4): FlamePiece => ({
  s: 'flame',
  x,
  y,
  z,
  r,
  h,
});
const crystal = (
  x: number,
  y: number,
  z: number,
  r: number,
  h: number,
  c: string = A.crystal,
): CrystalPiece => ({ s: 'crystal', x, y, z, r, h, c });
const flag = (x: number, y: number, z: number, w: number, h: number, c: string): FlagPiece => ({
  s: 'flag',
  x,
  y,
  z,
  w,
  h,
  c,
});
const ball = (x: number, y: number, r: number, z: number, c: string): Piece => ({
  s: 'ball',
  x,
  y,
  r,
  z,
  c,
});
const disc = (x: number, y: number, r: number, z: number, c: string, glow = false): Piece => ({
  s: 'disc',
  x,
  y,
  r,
  z,
  c,
  ...(glow ? { glow } : {}),
});

/** Windows on a wall, lit at dusk and night. */
const windows = (rows: number, cols: number, lit = 0.7): FaceMarks => ({
  kind: 'win',
  rows,
  cols,
  c: A.window,
  on: A.lit,
  lit,
  z0: 0.2,
  z1: 0.85,
});

/** A post with a flame on it. */
function torch(x: number, y: number, h = 0.36, z = 0): ArenaPiece[] {
  return [
    rod([x, y, z], [x, y, z + h], 0.03, A.timberDark),
    cyl(x, y, 0.035, z + h, 0.035, A.iron),
    flame(x, y, z + h + 0.035, 0.04),
  ];
}

/** A pole with a flag flying from its top. */
function banner(x: number, y: number, h: number, c: string, z = 0, size = 1): ArenaPiece[] {
  return [
    rod([x, y, z], [x, y, z + h], 0.025, A.timberDark),
    flag(x, y, z + h - 0.02, 0.2 * size, 0.12 * size, c),
  ];
}

/** A campfire: a ring of stones, crossed logs and the flame, `s` its size. */
function campfire(cx: number, cy: number, s: number): ArenaPiece[] {
  const out: ArenaPiece[] = [];
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    out.push(
      ball(cx + Math.cos(a) * 0.15 * s, cy + Math.sin(a) * 0.15 * s, 0.04 * s, 0, A.stoneDark),
    );
  }
  out.push(
    rod([cx - 0.1 * s, cy - 0.06 * s, 0.03], [cx + 0.1 * s, cy + 0.06 * s, 0.05], 0.05 * s, A.log),
    rod([cx - 0.1 * s, cy + 0.06 * s, 0.03], [cx + 0.1 * s, cy - 0.06 * s, 0.05], 0.05 * s, A.log),
    flame(cx, cy, 0.04, 0.07 * s, 0.2 * s),
  );
  return out;
}

/** Merlons along a wall top's edges, `size` apart. */
function crenels(
  x: number,
  y: number,
  w: number,
  d: number,
  z: number,
  c: string,
  size = 0.08,
): Piece[] {
  const out: Piece[] = [];
  const run = (x0: number, y0: number, x1: number, y1: number) => {
    const len = Math.hypot(x1 - x0, y1 - y0);
    const n = Math.max(1, Math.floor(len / (size * 2)));
    for (let i = 0; i < n; i++) {
      const f = (i + 0.25) / n;
      out.push(
        box(x0 + (x1 - x0) * f - size / 2, y0 + (y1 - y0) * f - size / 2, size, size, z, size, c),
      );
    }
  };
  const m = size / 2;
  run(x + m, y + m, x + w - m, y + m);
  run(x + m, y + d - m, x + w - m, y + d - m);
  run(x + m, y + m, x + m, y + d - m);
  run(x + w - m, y + m, x + w - m, y + d - m);
  return out;
}

/** A round tower: its wall, a crown of merlons or a cone roof, slit windows, a flag. */
function roundTower(
  cx: number,
  cy: number,
  r: number,
  h: number,
  wall: string,
  o: { roof?: string; roofH?: number; flagC?: string; rows?: number; z?: number } = {},
): ArenaPiece[] {
  const z = o.z ?? 0;
  const out: ArenaPiece[] = [cyl(cx, cy, r, z, h, wall, tint(wall, 0.1))];
  const rows = o.rows ?? Math.max(1, Math.floor(h / 0.4));
  // The slits on the two faces the eye sees, lit at night.
  out.push(
    box(cx - 0.03, cy + r * 0.86, 0.06, r * 0.2, z + h * 0.15, h * 0.7, wall, {
      marks: windows(rows, 1, 0.8),
    }),
    box(cx + r * 0.86, cy - 0.03, r * 0.2, 0.06, z + h * 0.15, h * 0.7, wall, {
      marks: windows(rows, 1, 0.6),
    }),
  );
  if (o.roof) {
    out.push(cyl(cx, cy, r * 1.12, z + h, 0.05, shade(wall, 0.1)));
    const rh = o.roofH ?? r * 2.4;
    out.push(cone(cx, cy, r * 1.2, z + h + 0.05, rh, o.roof));
    if (o.flagC) out.push(...banner(cx, cy, 0.22, o.flagC, z + h + 0.05 + rh * 0.9, 0.8));
  } else {
    out.push(cyl(cx, cy, r * 1.1, z + h, 0.06, wall, tint(wall, 0.06)));
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      out.push(
        box(
          cx + Math.cos(a) * r * 0.95 - 0.035,
          cy + Math.sin(a) * r * 0.95 - 0.035,
          0.07,
          0.07,
          z + h + 0.06,
          0.07,
          wall,
        ),
      );
    }
    if (o.flagC) out.push(...banner(cx, cy, 0.34, o.flagC, z + h + 0.06));
  }
  return out;
}

/** A hut or hall: its walls, a gabled roof with eaves, a door on the front, lit windows. */
function hut(
  x: number,
  y: number,
  w: number,
  d: number,
  h: number,
  wall: string,
  roof: string,
  o: { roofH?: number; rows?: number; door?: string } = {},
): ArenaPiece[] {
  const along = w >= d;
  const cols = Math.max(1, Math.round((along ? w : d) * 2.4));
  const out: ArenaPiece[] = [
    box(x, y, w, d, 0, h, wall, {
      roof: false,
      ...(o.rows ? { marks: windows(o.rows, cols) } : {}),
    }),
  ];
  const e = 0.05;
  out.push(
    gable(
      x - e,
      y - e,
      w + 2 * e,
      d + 2 * e,
      h,
      o.roofH ?? Math.min(w, d) * 0.55,
      roof,
      along,
      wall,
    ),
  );
  // The door, on the long side towards the eye.
  out.push(
    along
      ? box(
          x + w / 2 - 0.07,
          y + d - 0.005,
          0.14,
          0.02,
          0,
          Math.min(0.26, h * 0.8),
          o.door ?? A.timberDark,
        )
      : box(
          x + w - 0.005,
          y + d / 2 - 0.07,
          0.02,
          0.14,
          0,
          Math.min(0.26, h * 0.8),
          o.door ?? A.timberDark,
        ),
  );
  return out;
}

/** A bell tent: a short canvas wall, its cone, a pennant. Tier 0 pitches a low ridge tent. */
function tent(
  cx: number,
  cy: number,
  r: number,
  c: string,
  trim: string,
  kind: number,
): ArenaPiece[] {
  if (kind === 0)
    return [
      gable(cx - r, cy - r * 0.7, r * 2, r * 1.4, 0, r * 1.1, c, true),
      box(cx - r - 0.01, cy - 0.04, 0.02, 0.08, 0, r * 0.7, A.timberDark),
    ];
  const wallH = r * 0.6,
    roofH = r * (kind === 1 ? 1.4 : 1.7);
  const out: ArenaPiece[] = [
    cyl(cx, cy, r, 0, wallH, kind === 2 ? trim : c),
    cone(cx, cy, r * 1.08, wallH, roofH, c),
    box(cx - 0.05, cy + r * 0.9, 0.1, 0.06, 0, wallH * 0.9, shade(c, 0.45)),
  ];
  out.push(...banner(cx, cy, 0.14, trim, wallH + roofH - 0.02, 0.7));
  return out;
}

/** A horse standing along x, its head towards +x when `dir` is 1. */
function horse(x: number, y: number, c: string, dir: 1 | -1 = 1): ArenaPiece[] {
  const out: ArenaPiece[] = [box(x - 0.13, y - 0.05, 0.26, 0.1, 0.12, 0.1, c)];
  for (const dx of [-0.1, 0.1])
    for (const dy of [-0.03, 0.03])
      out.push(rod([x + dx, y + dy, 0], [x + dx, y + dy, 0.13], 0.03, shade(c, 0.2)));
  out.push(
    rod([x + dir * 0.12, y, 0.2], [x + dir * 0.18, y, 0.3], 0.06, c),
    box(x + dir * 0.18 - 0.05, y - 0.03, 0.1, 0.06, 0.27, 0.05, c),
    rod([x - dir * 0.13, y, 0.2], [x - dir * 0.17, y, 0.08], 0.025, shade(c, 0.35)),
  );
  return out;
}

/** A pile of logs lying along x. */
function logPile(x: number, y: number, n: number): ArenaPiece[] {
  const out: ArenaPiece[] = [];
  let k = 0;
  for (let row = 0; row < 3 && k < n; row++)
    for (let i = 0; i < 3 - row && k < n; i++, k++) {
      const yy = y + 0.05 + i * 0.1 + row * 0.05;
      out.push(rod([x, yy, 0.05 + row * 0.08], [x + 0.5, yy, 0.05 + row * 0.08], 0.08, A.log));
    }
  return out;
}

/** A pine: a trunk and two cones. */
function pine(x: number, y: number, s: number): ArenaPiece[] {
  return [
    cyl(x, y, 0.03 * s, 0, 0.12 * s, A.timberDark),
    cone(x, y, 0.2 * s, 0.1 * s, 0.32 * s, A.pine),
    cone(x, y, 0.15 * s, 0.3 * s, 0.26 * s, tint(A.pine, 0.06)),
  ];
}

/** A rough rock: a few boulders heaped. */
function boulders(x: number, y: number, s: number, c: string = A.rock, salt = 0): Piece[] {
  const out: Piece[] = [];
  for (let i = 0; i < 3; i++) {
    const a = hashXY(x, y, salt + i) * Math.PI * 2;
    out.push(
      ball(
        x + Math.cos(a) * 0.1 * s,
        y + Math.sin(a) * 0.1 * s,
        (0.08 + 0.06 * hashXY(x + i, y, salt)) * s,
        0,
        i ? shade(c, 0.08 * i) : c,
      ),
    );
  }
  return out;
}

/* ---------- the producers ---------- */

type Model = (p: IsoParcel, L: Look) => ArenaUnit[];

/** Each tile its own unit, from a maker given the tile, its place in the fill and the tier. */
const perTile =
  (make: (x: number, y: number, i: number, tier: number, p: IsoParcel) => ArenaPiece[]): Model =>
  (p) => {
    const tier = p.tier ?? 0;
    return tilesOf(p).map(([x, y], i) => ({
      x,
      y,
      w: 1,
      d: 1,
      pieces: make(x, y, i, tier, p),
      id: p.id ?? '',
    }));
  };

/**
 * A plot with a main building on the biggest rectangle from its back corner (`mainRect`), the
 * rest of its tiles taking its smaller kit.
 */
const site =
  (
    main: (r: Bounds, tier: number, p: IsoParcel) => ArenaPiece[],
    small: (x: number, y: number, i: number, tier: number, p: IsoParcel) => ArenaPiece[],
    cap: { w: number; h: number } = { w: 2, h: 2 },
  ): Model =>
  (p) => {
    const tier = p.tier ?? 0;
    const full = mainRect(p.tiles);
    const r: Bounds = {
      x: full.x,
      y: full.y,
      w: Math.min(cap.w, full.w),
      h: Math.min(cap.h, full.h),
    };
    const out: ArenaUnit[] = [];
    if (r.w && r.h)
      out.push({ x: r.x, y: r.y, w: r.w, d: r.h, pieces: main(r, tier, p), id: p.id ?? '' });
    let i = 0;
    for (const [x, y] of tilesOf(p)) {
      if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) continue;
      out.push({ x, y, w: 1, d: 1, pieces: small(x, y, i++, tier, p), id: p.id ?? '' });
    }
    return out;
  };

/** Campfires: a fire on every tile, growing from kindling to a great hearth with the tier. */
const campfires = perTile((x, y, i, tier) => {
  const g = steps(tier, 4, 3);
  const cx = x + 0.5,
    cy = y + 0.5;
  const out: ArenaPiece[] = [];
  if (g >= 3) {
    // A great hearth: a raised stone ring.
    out.push(cyl(cx, cy, 0.3, 0, 0.08, A.stone, A.stoneDark));
    out.push(...campfire(cx, cy, 1.5).map((q) => lift(q, 0.08)));
  } else out.push(...campfire(cx, cy, 1 + g * 0.25));
  // Log seats round it, and a cooking tripod from the cookfire on.
  out.push(
    rod([x + 0.12, y + 0.82, 0.04], [x + 0.42, y + 0.88, 0.04], 0.08, A.log),
    rod([x + 0.82, y + 0.15, 0.04], [x + 0.88, y + 0.45, 0.04], 0.08, A.log),
  );
  if (g >= 1 && i % 2 === 0) {
    for (const [dx, dy] of [
      [-0.2, -0.12],
      [0.2, -0.12],
      [0, 0.22],
    ] as const)
      out.push(rod([cx + dx, cy + dy, 0], [cx, cy, 0.42], 0.025, A.timberDark));
    out.push(cyl(cx, cy, 0.07, 0.24, 0.08, A.iron));
  }
  return out;
});

/** Lift a piece by `dz`. */
function lift<T extends ArenaPiece>(q: T, dz: number): T {
  if (q.s === 'rod')
    return { ...q, a: [q.a[0], q.a[1], q.a[2] + dz], b: [q.b[0], q.b[1], q.b[2] + dz] };
  return { ...q, z: (q as { z: number }).z + dz };
}

const TENTS = [A.canvas, A.canvasRed, A.canvas, A.canvasBlue, A.canvasGreen, A.canvasGold];

/** Tents: lean-tos, then bell tents, then striped pavilions, in mixed canvas. */
const tents = perTile((x, y, i, tier) => {
  const kind = tier < 3 ? 0 : tier < 8 ? 1 : 2;
  const c = TENTS[Math.floor(hashXY(x, y, 3) * TENTS.length)] as string;
  const trim = c === A.canvas ? A.canvasRed : A.canvas;
  const r = kind === 2 ? 0.34 : 0.28;
  const out = tent(x + 0.5, y + 0.48, r, c, trim, kind);
  if (i % 3 === 1) out.push(...torch(x + 0.88, y + 0.88, 0.3));
  return out;
});

/** Lumber camps: a timber hut with its log stack, log piles, stumps and pines about it. */
const lumberCamps = site(
  (r, tier) => {
    const out: ArenaPiece[] = hut(r.x + 0.15, r.y + 0.15, 0.7, 0.55, 0.3, A.timber, A.thatch, {
      rows: 1,
    });
    out.push(...logPile(r.x + 0.2, r.y + 0.72, 3 + steps(tier, 3, 3)));
    if (r.w > 1) {
      // A saw pit's frame, then a sawn-timber stack.
      out.push(
        rod([r.x + 1.2, r.y + 0.3, 0], [r.x + 1.2, r.y + 0.3, 0.4], 0.04, A.timberDark),
        rod([r.x + 1.8, r.y + 0.3, 0], [r.x + 1.8, r.y + 0.3, 0.4], 0.04, A.timberDark),
        rod([r.x + 1.15, r.y + 0.3, 0.38], [r.x + 1.85, r.y + 0.3, 0.38], 0.05, A.log),
        box(r.x + 1.2, r.y + 0.6, 0.6, 0.25, 0, 0.06 + 0.04 * steps(tier, 4, 3), A.plank),
      );
    }
    return out;
  },
  (x, y, i, tier) => {
    const k = Math.floor(hashXY(x, y, 5) * 3);
    if (k === 0) return logPile(x + 0.25, y + 0.3, 4 + steps(tier, 4, 2));
    if (k === 1)
      return [
        ...pine(x + 0.3, y + 0.35, 1.1),
        ...pine(x + 0.72, y + 0.62, 0.9),
        cyl(x + 0.7, y + 0.2, 0.06, 0, 0.06, A.log, A.logEnd),
      ];
    return [
      cyl(x + 0.3, y + 0.3, 0.07, 0, 0.07, A.log, A.logEnd),
      cyl(x + 0.65, y + 0.5, 0.06, 0, 0.05, A.log, A.logEnd),
      ...pine(x + 0.5, y + 0.78, 0.8 + i * 0.02),
    ];
  },
  { w: 2, h: 1 },
);

/** Quarries: a cut rock face stepping up with the tier, stacked blocks and a hoist. */
const quarries = site(
  (r, tier) => {
    const out: ArenaPiece[] = [];
    const tiers = 2 + steps(tier, 3, 3);
    const stone = tier >= 12 ? A.marble : A.rock;
    for (let t = 0; t < tiers; t++)
      out.push(
        box(
          r.x + 0.05,
          r.y + 0.05,
          r.w - 0.1,
          (r.h - 0.1) * (1 - t / (tiers + 1)),
          t * 0.16,
          0.16,
          shade(stone, t * 0.04),
        ),
      );
    // A timber hoist at the face's foot.
    const hx = r.x + r.w - 0.3,
      hy = r.y + r.h - 0.25;
    out.push(
      rod([hx - 0.12, hy, 0], [hx, hy, 0.6], 0.035, A.timber),
      rod([hx + 0.12, hy, 0], [hx, hy, 0.6], 0.035, A.timber),
      rod([hx, hy, 0.6], [hx - 0.35, hy - 0.3, 0.68], 0.03, A.timber),
      box(hx - 0.4, hy - 0.35, 0.1, 0.1, 0.3, 0.1, stone),
    );
    return out;
  },
  (x, y, i, tier) => {
    const stone = tier >= 12 ? A.marble : A.stone;
    const n = 2 + steps(tier, 4, 3);
    const out: ArenaPiece[] = [];
    for (let k = 0; k < n; k++)
      out.push(
        box(
          x + 0.2 + (k % 2) * 0.32,
          y + 0.25 + Math.floor(k / 4) * 0.3,
          0.28,
          0.22,
          (Math.floor(k / 2) % 2) * 0.14,
          0.14,
          shade(stone, (k % 3) * 0.04),
        ),
      );
    if (i % 2) out.push(...boulders(x + 0.75, y + 0.78, 0.8, A.rock, 9));
    return out;
  },
);

/** Watchtowers: lookout posts, then timber towers, then stone towers flying the flag. */
const watchtowers = perTile((x, y, i, tier) => {
  const cx = x + 0.5,
    cy = y + 0.5;
  const tall = 1 + steps(tier, 3, 4) * 0.18 + hashXY(x, y, 7) * 0.15;
  if (tier >= 10)
    return roundTower(cx, cy, 0.2, tall + 0.2, A.stone, { roof: A.slate, flagC: A.red });
  const out: ArenaPiece[] = [];
  const s = 0.16;
  for (const [dx, dy] of [
    [-s, -s],
    [s, -s],
    [-s, s],
    [s, s],
  ] as const)
    out.push(rod([cx + dx * 1.3, cy + dy * 1.3, 0], [cx + dx, cy + dy, tall], 0.04, A.timber));
  out.push(
    rod(
      [cx - s * 1.15, cy + s * 1.15, tall * 0.45],
      [cx + s * 1.15, cy + s * 1.15, tall * 0.45],
      0.025,
      A.timberDark,
    ),
  );
  out.push(box(cx - 0.22, cy - 0.22, 0.44, 0.44, tall, 0.04, A.plank));
  if (tier >= 4) out.push(box(cx - 0.2, cy - 0.2, 0.4, 0.4, tall + 0.04, 0.12, A.timber));
  out.push(cone(cx, cy, 0.3, tall + 0.2, 0.24, tier >= 4 ? A.tile : A.thatch, 4));
  if (i % 2 === 0) out.push(flame(cx + 0.14, cy + 0.14, tall + 0.05, 0.035));
  return out;
});

/** Forges: a stone forge with its chimney and glowing hearth; anvils, barrels and racks. */
const forges = site(
  (r, tier) => {
    const g = steps(tier, 5, 2);
    const w = Math.min(r.w, 1.4) - 0.2;
    const x = r.x + 0.1,
      y = r.y + 0.15;
    const out: ArenaPiece[] = hut(x, y, w, 0.6, 0.32 + g * 0.12, A.stoneDark, A.slate, { rows: 1 });
    // The chimney, its smoke drawn by the renderers off its top (`forgeTops`).
    out.push(box(x + 0.08, y + 0.08, 0.16, 0.16, 0, 0.75 + g * 0.2, A.stoneDark));
    // The open hearth on its front, the fire showing.
    out.push(box(x + w * 0.55, y + 0.59, 0.24, 0.03, 0.02, 0.18, A.ember, { glow: true }));
    out.push(flame(x + w * 0.67, y + 0.66, 0.02, 0.06, 0.12));
    out.push(...anvil(x + w * 0.35, y + 0.88));
    return out;
  },
  (x, y, i) =>
    i % 2
      ? [
          cyl(x + 0.3, y + 0.35, 0.1, 0, 0.18, A.log, A.water),
          cyl(x + 0.55, y + 0.3, 0.09, 0, 0.16, A.log),
          ...anvil(x + 0.5, y + 0.7),
        ]
      : [
          // A rack of blades.
          rod([x + 0.2, y + 0.5, 0.3], [x + 0.8, y + 0.5, 0.3], 0.03, A.timber),
          rod([x + 0.2, y + 0.5, 0], [x + 0.2, y + 0.5, 0.32], 0.03, A.timber),
          rod([x + 0.8, y + 0.5, 0], [x + 0.8, y + 0.5, 0.32], 0.03, A.timber),
          ...[0.32, 0.44, 0.56, 0.68].map((f) =>
            rod([x + f, y + 0.52, 0.02], [x + f, y + 0.52, 0.34], 0.02, A.steel),
          ),
          ...torch(x + 0.85, y + 0.85),
        ],
  { w: 2, h: 1 },
);

function anvil(x: number, y: number): ArenaPiece[] {
  return [cyl(x, y, 0.06, 0, 0.1, A.log), box(x - 0.08, y - 0.035, 0.16, 0.07, 0.1, 0.05, A.iron)];
}

/** Guard houses: a stone guardhouse growing to a gate fort; torches and spear racks. */
const guardHouses = site(
  (r, tier) => {
    const floors = 1 + steps(tier, 4, 3);
    const h = 0.32 + floors * 0.22;
    const w = r.w - 0.3,
      d = r.h - 0.3;
    const x = r.x + 0.15,
      y = r.y + 0.15;
    const out: ArenaPiece[] = [
      box(x, y, w, d, 0, h, A.stone, {
        marks: windows(floors, Math.max(2, Math.round(w * 3))),
        roof: true,
        top: A.stoneDark,
      }),
    ];
    out.push(...crenels(x, y, w, d, h, A.stone));
    out.push(box(x + w / 2 - 0.08, y + d - 0.01, 0.16, 0.02, 0, 0.26, A.timberDark));
    if (tier >= 8 && r.w >= 2)
      out.push(...roundTower(x + w, y + d, 0.18, h + 0.3, A.stone, { flagC: A.red }));
    return out;
  },
  (x, y, i) =>
    i % 2
      ? [...torch(x + 0.5, y + 0.5, 0.4), ...torch(x + 0.15, y + 0.85, 0.3)]
      : [
          rod([x + 0.2, y + 0.4, 0.28], [x + 0.8, y + 0.4, 0.28], 0.03, A.timber),
          ...[0.3, 0.45, 0.6, 0.75].map((f) =>
            rod([x + f, y + 0.42, 0], [x + f - 0.02, y + 0.42, 0.55], 0.018, A.timberDark),
          ),
          ...[0.3, 0.45, 0.6, 0.75].map((f) =>
            cone(x + f - 0.02, y + 0.42, 0.025, 0.55, 0.06, A.steel, 4),
          ),
        ],
);

/** Stables: a long timber stable with its stall doors; horses, hay and a rail. */
const stables = site(
  (r, tier) => {
    const g = steps(tier, 5, 2);
    const w = r.w - 0.2,
      d = 0.6;
    const wall = g >= 2 ? A.stone : A.timber;
    const out: ArenaPiece[] = hut(
      r.x + 0.1,
      r.y + 0.12,
      w,
      d,
      0.34,
      wall,
      g >= 1 ? A.tile : A.thatch,
      { roofH: 0.26 },
    );
    const n = Math.max(2, Math.round(w * 3));
    for (let k = 0; k < n; k++)
      out.push(
        box(
          r.x + 0.1 + ((k + 0.5) * w) / n - 0.06,
          r.y + 0.12 + d - 0.005,
          0.12,
          0.02,
          0,
          0.22,
          A.timberDark,
        ),
      );
    return out;
  },
  (x, y, i, tier) => {
    const c = A.horse[Math.floor(hashXY(x, y, 11) * A.horse.length)] as string;
    const out: ArenaPiece[] = [...horse(x + 0.45, y + 0.45, c, i % 2 ? 1 : -1)];
    if (tier >= 3) out.push(box(x + 0.65, y + 0.72, 0.22, 0.16, 0, 0.12, A.straw, { roof: true }));
    out.push(
      rod([x + 0.05, y + 0.95, 0.18], [x + 0.95, y + 0.95, 0.18], 0.025, A.timber),
      rod([x + 0.1, y + 0.95, 0], [x + 0.1, y + 0.95, 0.22], 0.03, A.timber),
      rod([x + 0.9, y + 0.95, 0], [x + 0.9, y + 0.95, 0.22], 0.03, A.timber),
    );
    return out;
  },
  { w: 3, h: 1 },
);

/** Archery ranges: straw butts with painted faces, a shooting line, a covered stand later. */
const archeryRanges = perTile((x, y, i, tier) => {
  const out: ArenaPiece[] = [];
  const cx = x + 0.5,
    cy = y + 0.3;
  out.push(
    rod([cx - 0.12, cy + 0.06, 0], [cx - 0.1, cy, 0.34], 0.03, A.timber),
    rod([cx + 0.12, cy + 0.06, 0], [cx + 0.1, cy, 0.34], 0.03, A.timber),
    box(cx - 0.15, cy - 0.04, 0.3, 0.06, 0.08, 0.28, A.straw),
    box(cx - 0.1, cy + 0.015, 0.2, 0.012, 0.12, 0.2, A.white),
    box(cx - 0.06, cy + 0.025, 0.12, 0.012, 0.16, 0.12, A.red),
    box(cx - 0.025, cy + 0.032, 0.05, 0.012, 0.195, 0.05, A.gold),
  );
  // The shooting line's pegs, and a covered stand from the long range on.
  out.push(rod([x + 0.1, y + 0.9, 0.02], [x + 0.9, y + 0.9, 0.02], 0.02, A.white));
  if (tier >= 6 && i % 3 === 0) {
    out.push(rod([x + 0.15, y + 0.75, 0], [x + 0.15, y + 0.75, 0.42], 0.03, A.timber));
    out.push(rod([x + 0.85, y + 0.75, 0], [x + 0.85, y + 0.75, 0.42], 0.03, A.timber));
    out.push({
      s: 'panel',
      x: x + 0.1,
      y: y + 0.65,
      w: 0.8,
      d: 0.25,
      z: 0.42,
      rise: 0.08,
      c: tier >= 12 ? A.canvasRed : A.canvas,
    });
  }
  if (i % 4 === 2) out.push(...banner(x + 0.9, y + 0.6, 0.5, A.canvasGold));
  return out;
});

/** Shrines: a small stone shrine with its flame, then a columned temple, then a domed one. */
const shrines = site(
  (r, tier) => {
    const g = steps(tier, 5, 2);
    const cx = r.x + r.w / 2,
      cy = r.y + r.h / 2;
    const s = Math.min(r.w, r.h);
    const out: ArenaPiece[] = [
      box(cx - 0.4 * s, cy - 0.35 * s, 0.8 * s, 0.7 * s, 0, 0.08, A.stoneLight, { roof: true }),
    ];
    const hw = 0.3 * s,
      hd = 0.24 * s;
    const colH = 0.36 + g * 0.14;
    for (const [dx, dy] of [
      [-hw, -hd],
      [hw, -hd],
      [-hw, hd],
      [hw, hd],
      [0, hd],
      [0, -hd],
    ] as const)
      out.push(cyl(cx + dx, cy + dy, 0.035, 0.08, colH, A.marble));
    out.push(
      box(
        cx - hw - 0.06,
        cy - hd - 0.06,
        2 * hw + 0.12,
        2 * hd + 0.12,
        0.08 + colH,
        0.06,
        A.marble,
        { roof: true },
      ),
    );
    if (g >= 2) out.push(ball(cx, cy, hd + 0.04, 0.08 + colH, A.gold));
    else
      out.push(
        gable(
          cx - hw - 0.08,
          cy - hd - 0.08,
          2 * hw + 0.16,
          2 * hd + 0.16,
          0.14 + colH,
          0.18,
          g ? A.gold : A.tile,
          true,
        ),
      );
    out.push(cyl(cx, cy, 0.07, 0.08, 0.14, A.stoneDark), flame(cx, cy, 0.22, 0.06, 0.16));
    return out;
  },
  (x, y, i) =>
    i % 2
      ? [
          // A stone lantern.
          box(x + 0.44, y + 0.44, 0.12, 0.12, 0, 0.26, A.stone),
          box(x + 0.4, y + 0.4, 0.2, 0.2, 0.26, 0.1, A.stoneLight),
          cone(x + 0.5, y + 0.5, 0.15, 0.36, 0.1, A.stone, 4),
          flame(x + 0.5, y + 0.5, 0.28, 0.035, 0.06),
          ...boulders(x + 0.2, y + 0.8, 0.5, A.rock, 13),
        ]
      : [
          cyl(x + 0.5, y + 0.5, 0.12, 0, 0.12, A.stone, A.stoneDark),
          flame(x + 0.5, y + 0.5, 0.12, 0.05, 0.1),
          ...[0, 1, 2, 3].map((k) =>
            cyl(x + 0.25 + k * 0.17, y + 0.82, 0.02, 0, 0.06 + (k % 2) * 0.03, A.white),
          ),
        ],
);

/** Training grounds: a sand yard of practice posts, weapon racks and a roped sparring ring. */
const trainingGrounds = site(
  (r, tier) => {
    const cx = r.x + r.w / 2,
      cy = r.y + r.h / 2;
    const R = Math.min(r.w, r.h) * 0.42;
    const out: ArenaPiece[] = [disc(cx, cy, R, 0, A.sand)];
    const n = 10;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      out.push(
        rod(
          [cx + Math.cos(a) * R, cy + Math.sin(a) * R, 0],
          [cx + Math.cos(a) * R, cy + Math.sin(a) * R, 0.22],
          0.03,
          A.timber,
        ),
      );
    }
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2,
        b = ((k + 1) / n) * Math.PI * 2;
      out.push(
        rod(
          [cx + Math.cos(a) * R, cy + Math.sin(a) * R, 0.18],
          [cx + Math.cos(b) * R, cy + Math.sin(b) * R, 0.18],
          0.015,
          A.straw,
        ),
      );
    }
    if (tier >= 10)
      out.push(
        ...banner(r.x + 0.1, r.y + 0.1, 0.7, A.canvasRed),
        ...banner(r.x + r.w - 0.1, r.y + 0.1, 0.7, A.canvasBlue),
      );
    return out;
  },
  (x, y, i) => {
    if (i % 2)
      return [
        rod([x + 0.5, y + 0.5, 0], [x + 0.5, y + 0.5, 0.4], 0.05, A.timber),
        rod([x + 0.35, y + 0.5, 0.3], [x + 0.65, y + 0.5, 0.3], 0.04, A.timber),
        box(x + 0.44, y + 0.44, 0.12, 0.12, 0.16, 0.16, A.straw),
        ball(x + 0.5, y + 0.5, 0.06, 0.4, A.straw),
      ];
    return [
      rod([x + 0.2, y + 0.4, 0.3], [x + 0.8, y + 0.4, 0.3], 0.03, A.timber),
      rod([x + 0.2, y + 0.4, 0], [x + 0.2, y + 0.4, 0.32], 0.03, A.timber),
      rod([x + 0.8, y + 0.4, 0], [x + 0.8, y + 0.4, 0.32], 0.03, A.timber),
      ...[0.32, 0.46, 0.6].map((f) =>
        rod([x + f, y + 0.42, 0], [x + f + 0.02, y + 0.42, 0.42], 0.02, A.plank),
      ),
      box(x + 0.25, y + 0.7, 0.16, 0.06, 0, 0.2, A.canvasRed),
      box(x + 0.5, y + 0.7, 0.16, 0.06, 0, 0.2, A.canvasBlue),
    ];
  },
  { w: 3, h: 3 },
);

/** Great halls: a longhouse, then a timber hall, then a stone hall with its tower. */
const greatHalls = site(
  (r, tier) => {
    const g = steps(tier, 5, 2);
    const wall = g >= 2 ? A.stone : A.timber;
    const w = r.w - 0.2,
      d = Math.min(r.h - 0.2, 0.9);
    const out: ArenaPiece[] = hut(
      r.x + 0.1,
      r.y + 0.1,
      w,
      d,
      0.4 + g * 0.1,
      wall,
      g >= 1 ? A.slate : A.thatch,
      {
        rows: 1,
        roofH: d * 0.75,
      },
    );
    if (g >= 2)
      out.push(
        ...roundTower(r.x + 0.3, r.y + 0.3, 0.2, 1.2, A.stone, { roof: A.slate, flagC: A.red }),
      );
    else out.push(...banner(r.x + r.w - 0.2, r.y + 0.2, 0.9, A.red));
    return out;
  },
  (x, y, i) =>
    i % 2
      ? [
          box(x + 0.15, y + 0.4, 0.7, 0.14, 0.14, 0.04, A.plank),
          box(x + 0.2, y + 0.45, 0.05, 0.05, 0, 0.14, A.timberDark),
          box(x + 0.75, y + 0.45, 0.05, 0.05, 0, 0.14, A.timberDark),
          ...torch(x + 0.85, y + 0.85),
        ]
      : [
          cyl(x + 0.3, y + 0.3, 0.09, 0, 0.18, A.log),
          cyl(x + 0.52, y + 0.32, 0.09, 0, 0.18, A.log),
          cyl(x + 0.4, y + 0.5, 0.09, 0, 0.18, A.log),
        ],
  { w: 3, h: 2 },
);

const STALLS = [A.canvasRed, A.canvasBlue, A.canvasGold, A.canvasGreen];

/** Trading posts: market stalls with their awnings, crates and sacks, a trade hall later. */
const tradingPosts = site(
  (r, tier) => {
    if (tier < 8) return stall(r.x, r.y, 0);
    return hut(r.x + 0.1, r.y + 0.12, r.w - 0.2, Math.min(0.8, r.h - 0.25), 0.4, A.timber, A.tile, {
      rows: 1,
      door: A.canvasRed,
    });
  },
  (x, y, i) => stall(x, y, i + 1),
  { w: 2, h: 1 },
);

function stall(x: number, y: number, i: number): ArenaPiece[] {
  const c = STALLS[i % STALLS.length] as string;
  const out: ArenaPiece[] = [box(x + 0.18, y + 0.35, 0.64, 0.3, 0, 0.18, A.plank, { roof: true })];
  for (const [dx, dy] of [
    [0.18, 0.3],
    [0.82, 0.3],
    [0.18, 0.7],
    [0.82, 0.7],
  ] as const)
    out.push(rod([x + dx, y + dy, 0], [x + dx, y + dy, 0.4], 0.025, A.timberDark));
  out.push(gable(x + 0.12, y + 0.26, 0.76, 0.48, 0.4, 0.14, c, true));
  out.push(
    box(x + 0.25, y + 0.42, 0.12, 0.12, 0.18, 0.08, A.gold),
    box(x + 0.5, y + 0.42, 0.12, 0.12, 0.18, 0.06, A.canvasGreen),
  );
  out.push(
    box(x + 0.66, y + 0.76, 0.16, 0.14, 0, 0.12, A.plank),
    ball(x + 0.3, y + 0.82, 0.07, 0, A.straw),
  );
  return out;
}

/** Crystal mines: a mine mouth in a rock heap; crystal clusters glowing, bigger with the tier. */
const crystalMines = site(
  (r, tier) => {
    const out: ArenaPiece[] = [];
    const cx = r.x + 0.5,
      cy = r.y + 0.5;
    out.push(...boulders(cx, cy - 0.1, 2.2, A.rockDark, 21));
    out.push(
      rod([cx - 0.16, cy + 0.3, 0], [cx - 0.16, cy + 0.3, 0.32], 0.05, A.timber),
      rod([cx + 0.16, cy + 0.3, 0], [cx + 0.16, cy + 0.3, 0.32], 0.05, A.timber),
      rod([cx - 0.2, cy + 0.3, 0.32], [cx + 0.2, cy + 0.3, 0.32], 0.05, A.timber),
      box(cx - 0.12, cy + 0.26, 0.24, 0.02, 0, 0.3, '#201C1A'),
      crystal(cx + 0.25, cy + 0.32, 0, 0.06, 0.2 + steps(tier, 4, 3) * 0.05),
    );
    return out;
  },
  (x, y, i, tier) => {
    const g = steps(tier, 4, 3);
    const out: ArenaPiece[] = [...boulders(x + 0.5, y + 0.55, 1.2, A.rockDark, 17 + i)];
    const n = 2 + g;
    for (let k = 0; k < n; k++) {
      const a = hashXY(x + k, y, 23) * Math.PI * 2;
      out.push(
        crystal(
          x + 0.5 + Math.cos(a) * 0.22,
          y + 0.5 + Math.sin(a) * 0.22,
          0,
          0.05 + g * 0.012,
          0.18 + g * 0.06 + hashXY(x, y + k, 24) * 0.1,
          k % 3 === 2 ? A.violet : A.crystal,
        ),
      );
    }
    return out;
  },
  { w: 1, h: 1 },
);

/** Libraries: a stone library with tall windows and a reading tower; lanterns and benches. */
const libraries = site(
  (r, tier) => {
    const floors = 1 + steps(tier, 5, 2);
    const w = r.w - 0.2,
      d = Math.min(r.h - 0.2, 1.4);
    const out: ArenaPiece[] = hut(
      r.x + 0.1,
      r.y + 0.1,
      w,
      d,
      0.3 + floors * 0.2,
      A.stoneLight,
      A.slate,
      { rows: floors },
    );
    out.push(
      ...roundTower(r.x + w, r.y + 0.3, 0.16, 0.9 + floors * 0.2, A.stoneLight, {
        roof: '#3E5A7A',
        flagC: '#3E5A7A',
      }),
    );
    return out;
  },
  (x, y) => [
    box(x + 0.2, y + 0.45, 0.5, 0.12, 0.12, 0.03, A.plank),
    box(x + 0.22, y + 0.47, 0.04, 0.08, 0, 0.12, A.timberDark),
    box(x + 0.64, y + 0.47, 0.04, 0.08, 0, 0.12, A.timberDark),
    ...torch(x + 0.82, y + 0.8, 0.34),
  ],
  { w: 2, h: 2 },
);

/** Wizard towers: slender stone towers, violet roofs, an orb glowing at the top; taller with the tier. */
const wizardTowers = perTile((x, y, i, tier) => {
  const tall = 0.9 + steps(tier, 2, 6) * 0.12 + hashXY(x, y, 31) * 0.2;
  const cx = x + 0.5,
    cy = y + 0.5;
  if (i % 2 === 1 && tier < 6)
    return [
      // A hedge wizard's hut, its chimney glowing.
      ...hut(x + 0.2, y + 0.25, 0.55, 0.5, 0.28, A.stoneDark, '#5E4A8A', { rows: 1 }),
      crystal(x + 0.75, y + 0.8, 0, 0.04, 0.14, A.violet),
    ];
  const out = roundTower(cx, cy, 0.17, tall, A.stoneLight, { roof: '#5E4A8A', roofH: 0.5 });
  out.push(crystal(cx, cy, tall + 0.6, 0.06, 0.16, A.violet));
  if (tier >= 10)
    out.push(
      box(cx - 0.24, cy - 0.24, 0.48, 0.48, tall * 0.55, 0.04, A.stone),
      ...crenels(cx - 0.24, cy - 0.24, 0.48, 0.48, tall * 0.55 + 0.04, A.stone, 0.06),
    );
  return out;
});

/** Dragon roosts: a rocky crag with a nest and its eggs; a great dragon resting there later. */
const dragonRoosts = site(
  (r, tier) => {
    const cx = r.x + r.w / 2,
      cy = r.y + r.h / 2;
    const out: ArenaPiece[] = [];
    const s = Math.min(r.w, r.h);
    const crag = 0.3 + steps(tier, 4, 3) * 0.15;
    out.push(
      box(cx - 0.45 * s, cy - 0.4 * s, 0.9 * s, 0.8 * s, 0, crag, A.rockDark, {
        roof: true,
        top: A.rock,
      }),
    );
    out.push(...boulders(cx - 0.4 * s, cy + 0.35 * s, 1.6, A.rock, 41));
    out.push(...boulders(cx + 0.45 * s, cy - 0.3 * s, 1.4, A.rock, 43));
    // The nest on top.
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2,
        b = ((k + 1) / 8) * Math.PI * 2;
      out.push(
        rod(
          [cx + Math.cos(a) * 0.22, cy + Math.sin(a) * 0.22, crag + 0.04],
          [cx + Math.cos(b) * 0.22, cy + Math.sin(b) * 0.22, crag + 0.04],
          0.05,
          A.log,
        ),
      );
    }
    if (tier >= 12 && s >= 2) out.push(...dragon(cx, cy, crag + 0.02));
    else
      for (let k = 0; k < 3; k++)
        out.push(
          ball(
            cx + (k - 1) * 0.09,
            cy + (k % 2) * 0.05,
            0.06,
            crag + 0.02,
            k === 1 ? '#C98A5A' : '#8A5A7A',
          ),
        );
    return out;
  },
  (x, y, i) => [
    ...boulders(x + 0.4, y + 0.45, 1.4, A.rockDark, 47 + i),
    ...(i % 2
      ? [
          ball(x + 0.75, y + 0.75, 0.05, 0, A.bone),
          rod([x + 0.6, y + 0.8, 0.02], [x + 0.85, y + 0.65, 0.02], 0.025, A.bone),
        ]
      : []),
  ],
  { w: 2, h: 2 },
);

/** A dragon at rest, curled facing +x, its wings folded. Original form: no game's. */
function dragon(cx: number, cy: number, z: number): ArenaPiece[] {
  return [
    box(cx - 0.3, cy - 0.12, 0.5, 0.24, z, 0.2, A.dragon, {
      roof: true,
      top: shade(A.dragon, 0.08),
    }),
    box(cx - 0.26, cy - 0.1, 0.42, 0.2, z, 0.06, A.dragonBelly),
    rod([cx + 0.18, cy, z + 0.16], [cx + 0.32, cy + 0.05, z + 0.42], 0.08, A.dragon),
    box(cx + 0.28, cy - 0.04, 0.16, 0.1, z + 0.38, 0.08, A.dragon),
    cone(cx + 0.31, cy - 0.03, 0.02, z + 0.46, 0.06, A.bone, 4),
    cone(cx + 0.31, cy + 0.05, 0.02, z + 0.46, 0.06, A.bone, 4),
    rod([cx - 0.3, cy, z + 0.08], [cx - 0.55, cy + 0.22, z + 0.03], 0.06, A.dragon),
    rod([cx - 0.55, cy + 0.22, z + 0.03], [cx - 0.4, cy + 0.38, z + 0.02], 0.04, A.dragon),
    { s: 'panel', x: cx - 0.24, y: cy - 0.3, w: 0.36, d: 0.2, z: z + 0.18, rise: 0.22, c: A.wing },
    { s: 'panel', x: cx - 0.24, y: cy + 0.1, w: 0.36, d: 0.2, z: z + 0.18, rise: -0.1, c: A.wing },
  ];
}

/** Spirit springs: a pale glowing pool in a stone ring, standing stones round it. */
const spiritSprings = site(
  (r, tier) => {
    const cx = r.x + r.w / 2,
      cy = r.y + r.h / 2;
    const R = Math.min(r.w, r.h) * 0.36;
    const out: ArenaPiece[] = [
      disc(cx, cy, R + 0.08, 0, A.stone),
      disc(cx, cy, R, 0.012, A.spirit, true),
    ];
    const n = 5 + steps(tier, 4, 3);
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + 0.3;
      out.push(
        box(
          cx + Math.cos(a) * (R + 0.3) - 0.05,
          cy + Math.sin(a) * (R + 0.3) - 0.04,
          0.1,
          0.08,
          0,
          0.36 + (k % 2) * 0.12,
          A.stoneDark,
          { roof: true, top: A.stone },
        ),
      );
    }
    out.push(crystal(cx, cy, 0.3, 0.05, 0.12, A.spirit));
    return out;
  },
  (x, y, i) => [
    disc(x + 0.5, y + 0.5, 0.24, 0.01, A.spirit, true),
    ...boulders(x + 0.2, y + 0.25, 0.6, A.stone, 51 + i),
    crystal(x + 0.75, y + 0.72, 0.22, 0.03, 0.08, A.spirit),
  ],
);

/** Star spires: tall slender spires, a star glowing at each tip; higher with the tier. */
const starSpires = perTile((x, y, i, tier) => {
  const cx = x + 0.5,
    cy = y + 0.5;
  const tall = 0.9 + steps(tier, 2, 6) * 0.14 + hashXY(x, y, 61) * 0.2;
  if (tier < 3 && i % 2)
    return [
      box(cx - 0.12, cy - 0.12, 0.24, 0.24, 0, 0.3, A.stoneLight),
      ...banner(cx, cy, 0.3, A.canvasBlue, 0.3),
    ];
  return [
    box(cx - 0.16, cy - 0.16, 0.32, 0.32, 0, 0.14, A.stone, { roof: true }),
    cone(cx, cy, 0.12, 0.14, tall, A.marble, 4),
    crystal(cx, cy, tall + 0.14, 0.07, 0.18, A.star),
  ];
});

/** Each producer's own look, by id. One with none takes a plain timber hut. */
export const MODELS: Record<string, Model> = {
  campfires,
  tents,
  lumber_camps: lumberCamps,
  quarries,
  watchtowers,
  forges,
  guard_houses: guardHouses,
  stables,
  archery_ranges: archeryRanges,
  shrines,
  training_grounds: trainingGrounds,
  great_halls: greatHalls,
  trading_posts: tradingPosts,
  crystal_mines: crystalMines,
  libraries,
  wizard_towers: wizardTowers,
  dragon_roosts: dragonRoosts,
  spirit_springs: spiritSprings,
  star_spires: starSpires,
};

const plainHut = perTile((x, y) => hut(x + 0.2, y + 0.2, 0.6, 0.5, 0.3, A.timber, A.thatch));

/** A producer's plot as units. Empty for land with no producer on it. */
export function plotUnits(p: IsoParcel, L: Look): ArenaUnit[] {
  if (!p.id || p.t !== 'field' || !p.tiles.length) return [];
  return (MODELS[p.id] ?? plainHut)(p, L);
}

/** The tops of the forges' chimneys, where their smoke rises. */
export function forgeTops(p: IsoParcel): Array<[number, number, number]> {
  if (p.id !== 'forges') return [];
  const out: Array<[number, number, number]> = [];
  for (const u of plotUnits(p, { night: false, winter: false }))
    for (const q of u.pieces)
      if (q.s === 'box' && q.w === 0.16 && q.d === 0.16)
        out.push([q.x + 0.08, q.y + 0.08, q.z + q.h]);
  return out;
}

/** The campfires' flames, where their smoke rises: one per fire. */
export function fireTops(p: IsoParcel): Array<[number, number, number]> {
  if (p.id !== 'campfires') return [];
  const out: Array<[number, number, number]> = [];
  for (const u of plotUnits(p, { night: false, winter: false }))
    for (const q of u.pieces) if (q.s === 'flame') out.push([q.x, q.y, q.z + q.h]);
  return out;
}

/* ---------- the yard, the chains and the marks ---------- */

/** D291: the walls and roof decorations, from `look` (`walls+roof`). */
function decorOf(look?: string): { wall?: string; roof?: string } {
  const [b, r] = (look ?? '').split('+');
  return {
    ...(WALLS[b ?? ''] ? { wall: WALLS[b ?? ''] } : {}),
    ...(ROOFS[r ?? ''] ? { roof: ROOFS[r ?? ''] } : {}),
  };
}

/** How many storeys a chain building's producer's milestones give it (D268's `style`). */
function styleFloors(style?: string): number {
  return style === 'stone' ? 3 : style === 'timber' ? 2 : 1;
}

/**
 * The yard's two buildings and each chain's building. The house is the keep: a square tower
 * with its hall; the barn the vault, a stone storehouse wider with its level. A chain's
 * building has its own silhouette by what it is, taller as its producer grows.
 */
export function buildingUnits(b: IsoBuildingAt): ArenaUnit[] {
  const dec = decorOf(b.look);
  const { x, y, w, h } = b;
  const i = { x: x + 0.08, y: y + 0.08, w: w - 0.16, d: h - 0.16 };
  const unit = (ps: ArenaPiece[]): ArenaUnit[] => [
    { x, y, w, d: h, pieces: ps, ...(b.id ? { id: b.id } : {}) },
  ];
  const wall = dec.wall,
    roof = dec.roof;
  if (b.kind === 'house') {
    const out: ArenaPiece[] = [];
    const kw = i.w * 0.55,
      kd = i.d * 0.6;
    const kc = wall ?? A.stone;
    // The hall along the front, then the keep's tower behind it.
    out.push(
      ...hut(i.x, i.y + kd * 0.55, i.w, i.d - kd * 0.55, 0.42, wall ?? A.timber, roof ?? A.thatch, {
        rows: 1,
      }),
    );
    out.push(
      box(i.x + 0.05, i.y, kw, kd, 0, 1.25, kc, {
        marks: windows(3, 3),
        roof: true,
        top: shade(kc, 0.08),
      }),
    );
    out.push(...crenels(i.x + 0.05, i.y, kw, kd, 1.25, kc, 0.09));
    out.push(...banner(i.x + 0.05 + kw / 2, i.y + kd / 2, 0.5, A.red, 1.25, 1.2));
    out.push(...torch(i.x + i.w * 0.75, i.y + i.d + 0.02, 0.32));
    return unit(out);
  }
  if (b.kind === 'barn') {
    const out: ArenaPiece[] = hut(
      i.x,
      i.y + 0.1,
      i.w,
      i.d - 0.2,
      0.5,
      wall ?? A.stoneDark,
      roof ?? A.slate,
      { rows: 1, roofH: 0.35, door: A.iron },
    );
    // Iron-bound chests by the door.
    for (let k = 0; k < Math.min(3, Math.round(i.w)); k++)
      out.push(
        box(i.x + 0.1 + k * 0.3, i.y + i.d - 0.05, 0.22, 0.14, 0, 0.13, A.timber, {
          roof: true,
          top: A.gold,
        }),
      );
    return unit(out);
  }
  return unit(chainPieces(b.chain ?? '', i, styleFloors(b.style), wall, roof));
}

/** A chain building's pieces, by its chain's id. */
function chainPieces(
  c: string,
  i: { x: number; y: number; w: number; d: number },
  floors: number,
  wall?: string,
  roof?: string,
): ArenaPiece[] {
  const h = 0.26 + floors * 0.12;
  const W = wall ?? A.timber,
    R = roof ?? A.thatch;
  const cx = i.x + i.w / 2,
    cy = i.y + i.d / 2;
  switch (c) {
    case 'temple':
    case 'healers_house':
    case 'bathhouse':
      return [
        ...hut(
          i.x,
          i.y,
          i.w,
          i.d,
          h + 0.1,
          wall ?? A.stoneLight,
          roof ?? (c === 'temple' ? A.gold : A.slate),
          { rows: 1 },
        ),
        ...(c === 'temple' ? [ball(cx, cy, 0.18, h + 0.3, A.gold)] : []),
        ...(c === 'bathhouse' ? [cyl(i.x + 0.15, i.y + 0.15, 0.06, 0, h + 0.5, A.stoneDark)] : []),
      ];
    case 'arena': {
      // A round tourney ring: a low wall of stands round sand.
      const R0 = Math.min(i.w, i.d) / 2;
      const out: ArenaPiece[] = [disc(cx, cy, R0, 0, A.sand)];
      const n = 14;
      for (let k = 0; k < n; k++) {
        const a = (k / n) * Math.PI * 2;
        out.push(
          box(
            cx + Math.cos(a) * R0 * 0.9 - 0.07,
            cy + Math.sin(a) * R0 * 0.9 - 0.07,
            0.14,
            0.14,
            0,
            0.2 + floors * 0.06,
            k % 2 ? W : shade(W, 0.12),
          ),
        );
      }
      out.push(
        ...banner(cx, cy - R0 * 0.9, 0.5, A.red),
        ...banner(cx, cy + R0 * 0.9, 0.5, A.canvasBlue),
      );
      return out;
    }
    case 'armoury':
    case 'crystal_cutter':
      return [
        box(i.x, i.y, i.w, i.d, 0, h + 0.1, wall ?? A.stoneDark, {
          roof: true,
          top: A.slate,
          marks: windows(floors, 2),
        }),
        ...crenels(i.x, i.y, i.w, i.d, h + 0.1, wall ?? A.stoneDark),
        ...(c === 'crystal_cutter' ? [crystal(cx, cy, h + 0.12, 0.06, 0.18)] : []),
      ];
    case 'signal_post':
    case 'observatory':
    case 'spell_school':
    case 'dragon_keep': {
      const roofC = c === 'spell_school' ? '#5E4A8A' : c === 'dragon_keep' ? A.dragon : A.slate;
      const r = Math.min(i.w, i.d) * (c === 'dragon_keep' ? 0.4 : 0.3);
      const out = roundTower(
        cx,
        cy,
        r,
        0.6 + floors * 0.25,
        wall ?? A.stone,
        c === 'signal_post' ? {} : { roof: roofC, flagC: A.red },
      );
      if (c === 'signal_post')
        out.push(
          cyl(cx, cy, 0.1, 0.66 + floors * 0.25, 0.06, A.iron),
          flame(cx, cy, 0.72 + floors * 0.25, 0.08, 0.2),
        );
      if (c === 'observatory') out.push(ball(cx, cy, r * 0.8, 0.6 + floors * 0.25, A.steel));
      return out;
    }
    case 'gatehouse':
      return [
        ...roundTower(i.x + 0.2, cy, 0.18, h + 0.4, wall ?? A.stone),
        ...roundTower(i.x + i.w - 0.2, cy, 0.18, h + 0.4, wall ?? A.stone),
        box(i.x + 0.2, cy - 0.12, i.w - 0.4, 0.24, h, 0.2, wall ?? A.stone, { roof: true }),
        box(cx - 0.12, cy + 0.11, 0.24, 0.02, 0, h, A.timberDark),
      ];
    case 'pavilion':
    case 'canvas_works':
      return tent(cx, cy, Math.min(i.w, i.d) * 0.4, A.canvas, A.canvasRed, 2);
    case 'sawmill':
      return [
        box(i.x, i.y, i.w, i.d * 0.6, 0, h, W, { roof: false }),
        gable(i.x - 0.04, i.y - 0.04, i.w + 0.08, i.d * 0.6 + 0.08, h, 0.2, R),
        ...logPile(i.x + 0.05, i.y + i.d * 0.62, 5),
      ];
    case 'masons_yard':
      return [
        ...hut(i.x, i.y, i.w * 0.6, i.d * 0.6, h, wall ?? A.stoneDark, R),
        box(i.x + i.w * 0.62, i.y + 0.1, 0.25, 0.2, 0, 0.14, A.stone),
        box(i.x + i.w * 0.62, i.y + 0.4, 0.25, 0.2, 0, 0.28, A.stoneLight),
      ];
    case 'caravan_yard':
      return [
        box(i.x + 0.1, i.y + 0.2, i.w * 0.5, 0.3, 0.1, 0.18, A.plank),
        gable(i.x + 0.1, i.y + 0.2, i.w * 0.5, 0.3, 0.28, 0.16, A.canvas),
        cyl(i.x + 0.2, i.y + 0.52, 0.08, 0, 0.02, A.timberDark),
        ...horse(i.x + i.w * 0.75, i.y + i.d * 0.6, A.horse[0] as string),
      ];
    case 'feast_hall':
      return [
        ...hut(i.x, i.y, i.w, i.d, h + 0.1, W, R, { rows: floors }),
        ...banner(i.x + 0.1, i.y + 0.1, h + 0.5, A.gold),
      ];
    default:
      // A cookhouse, a fletcher's hut, a scriptorium, a saddlery, a bakehouse: a timber
      // building, a smoking chimney on the ones that cook.
      return [
        ...hut(i.x, i.y, i.w, i.d, h, W, R, { rows: floors }),
        ...(c === 'cookhouse' || c === 'bakehouse'
          ? [box(i.x + 0.1, i.y + 0.1, 0.12, 0.12, 0, h + 0.38, A.stoneDark)]
          : []),
      ];
  }
}

/** D245: a tournament's lasting mark, each its own small landmark. */
export function markUnits(kind: string, x: number, y: number): ArenaUnit[] {
  const p: ArenaPiece[] = [];
  switch (kind) {
    case 'founding_stone':
      p.push(box(x - 0.2, y - 0.14, 0.4, 0.28, 0, 0.36, A.stoneDark, { roof: true, top: A.stone }));
      p.push({
        s: 'panel',
        x: x - 0.14,
        y: y + 0.1,
        w: 0.28,
        d: 0.05,
        z: 0.12,
        rise: 0.16,
        c: A.gold,
      });
      break;
    case 'war_horn':
      p.push(
        rod([x - 0.08, y, 0], [x, y, 0.4], 0.04, A.timber),
        rod([x + 0.08, y, 0], [x, y, 0.4], 0.04, A.timber),
      );
      p.push(
        rod([x - 0.25, y, 0.44], [x + 0.25, y, 0.5], 0.06, A.bone),
        cone(x + 0.27, y, 0.08, 0.42, 0.12, A.gold),
      );
      break;
    case 'stone_lion':
      p.push(box(x - 0.22, y - 0.12, 0.44, 0.24, 0, 0.18, A.stone, { roof: true }));
      p.push(
        box(x - 0.16, y - 0.08, 0.3, 0.16, 0.18, 0.14, A.stoneLight),
        ball(x + 0.14, y, 0.1, 0.24, A.stoneLight),
      );
      break;
    case 'obelisk':
      p.push(box(x - 0.16, y - 0.16, 0.32, 0.32, 0, 0.1, A.stone, { roof: true }));
      p.push(
        box(x - 0.08, y - 0.08, 0.16, 0.16, 0.1, 1.0, A.stoneDark),
        cone(x, y, 0.11, 1.1, 0.16, A.gold, 4),
      );
      break;
    case 'fountain':
      p.push(cyl(x, y, 0.32, 0, 0.12, A.stone, A.stoneLight), disc(x, y, 0.27, 0.12, A.water));
      p.push(cyl(x, y, 0.05, 0.12, 0.3, A.stone), cyl(x, y, 0.12, 0.42, 0.04, A.stone));
      break;
    case 'banner_pole':
      p.push(cyl(x, y, 0.1, 0, 0.08, A.stone), ...banner(x, y, 1.1, A.red, 0.08, 1.4));
      break;
    case 'fire_bowl':
      for (const a of [0, 2.1, 4.2])
        p.push(
          rod([x + Math.cos(a) * 0.15, y + Math.sin(a) * 0.15, 0], [x, y, 0.32], 0.03, A.iron),
        );
      p.push(cyl(x, y, 0.15, 0.3, 0.08, A.iron), flame(x, y, 0.38, 0.1, 0.24));
      break;
    case 'stone_arch':
      p.push(
        box(x - 0.34, y - 0.08, 0.14, 0.16, 0, 0.62, A.stone),
        box(x + 0.2, y - 0.08, 0.14, 0.16, 0, 0.62, A.stone),
      );
      p.push(box(x - 0.38, y - 0.1, 0.76, 0.2, 0.62, 0.12, A.stoneLight, { roof: true }));
      break;
    case 'footbridge':
      p.push(disc(x, y, 0.36, 0, A.water));
      p.push(
        rod([x - 0.4, y, 0.02], [x, y, 0.16], 0.18, A.plank),
        rod([x, y, 0.16], [x + 0.4, y, 0.02], 0.18, A.plank),
      );
      p.push(
        rod([x - 0.4, y - 0.09, 0.14], [x, y - 0.09, 0.3], 0.02, A.timberDark),
        rod([x, y - 0.09, 0.3], [x + 0.4, y - 0.09, 0.14], 0.02, A.timberDark),
      );
      break;
    case 'beacon':
      p.push(
        rod([x - 0.1, y - 0.1, 0], [x, y, 1.1], 0.04, A.timber),
        rod([x + 0.1, y - 0.1, 0], [x, y, 1.1], 0.04, A.timber),
        rod([x, y + 0.12, 0], [x, y, 1.1], 0.04, A.timber),
      );
      p.push(cyl(x, y, 0.12, 1.1, 0.08, A.iron), flame(x, y, 1.18, 0.09, 0.24));
      break;
    default:
      p.push(box(x - 0.15, y - 0.15, 0.3, 0.3, 0, 0.3, A.stone, { roof: true }));
  }
  return [{ x: x - 0.4, y: y - 0.4, w: 0.8, d: 0.8, pieces: p }];
}

/* ---------- D420, D437: the stronghold's wall and gate, growing with the heart ---------- */

/**
 * D437: how far the wall has grown, the heart's own form (`heartForm`), read from the parcels so
 * both renderers ask it of the same thing: the producers on the land (each plot with tiles) and
 * the festivals held (each mark, and each memorial on a monument ground). 0 a timber palisade,
 * 1 a curtain wall with its walkway, merlons, braziers and a gatehouse, 2 corner towers as well.
 */
export function fortLevel(parcels: readonly IsoParcel[]): 0 | 1 | 2 {
  const producers = new Set(parcels.filter((p) => p.id && p.tiles.length).map((p) => p.id)).size;
  let years = 0;
  for (const p of parcels) years += (p.mark ? 1 : 0) + (p.ground?.marks.length ?? 0);
  return heartForm(producers, years);
}

/**
 * D420: the fence decoration, which a stronghold (no pens) wears on its wall: the stakes'
 * wood and their points, and the stone of the wall once it is built in stone. A stone wall or
 * battlements build the wall in stone from the first, whatever the stronghold's size.
 */
export interface Palisade {
  stake: string;
  /** Every other stake, for a painted palisade. */
  stake2?: string;
  tip: string;
  stone: string;
  /** Builds in stone from the first (`wallLevel`). */
  stoneFirst?: boolean;
}
export const PALISADES: Record<string, Palisade> = {
  palisade_timber: { stake: A.log, tip: A.logEnd, stone: A.stone },
  palisade_wattle: { stake: '#A8875A', tip: '#8E6E45', stone: '#C2B79E' },
  palisade_stone: { stake: A.stone, tip: A.stoneDark, stone: '#A9A396', stoneFirst: true },
  palisade_iron: { stake: A.iron, tip: A.steel, stone: A.stoneDark },
  palisade_thorn: { stake: '#4E5E3A', tip: '#2F3B24', stone: '#8E9680' },
  palisade_painted: { stake: '#B03A2E', stake2: '#EDE8DC', tip: A.gold, stone: '#E6E0D2' },
  palisade_battlements: { stake: A.stone, tip: A.stoneDark, stone: A.stoneLight, stoneFirst: true },
};
const palisadeOf = (fence?: string): Palisade =>
  PALISADES[fence ?? ''] ?? (PALISADES.palisade_timber as Palisade);

/** The wall's form for the heart's (`fortLevel`) and its fence decoration. */
export function wallLevel(level: number, fence?: string): number {
  return palisadeOf(fence).stoneFirst ? Math.max(1, level) : level;
}

/** D437: the curtain wall's height and thickness, its walkway and how far apart its braziers stand. */
const CURTAIN = { h: 0.55, t: 0.18, walk: 0.12, brazierEvery: 3 } as const;

/**
 * A run of the wall along one line on the ground, as one unit. `inward` points into the land, so
 * the merlons stand on the outer edge and the walkway runs along the inner side; at form 2 a
 * tower stands at each end asked for (`towers`), the corners. Braziers stand on the walkway, lit
 * at `night`.
 */
export function wallUnit(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  level: number,
  flagC: string,
  towers: { start: boolean; end: boolean } = { start: true, end: true },
  look: Palisade = palisadeOf(),
  o: { inward?: readonly [number, number]; night?: boolean } = {},
): ArenaUnit {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const ux = (x1 - x0) / (len || 1),
    uy = (y1 - y0) / (len || 1);
  const out: ArenaPiece[] = [];
  if (level <= 0) {
    // Sharpened stakes, close set.
    const n = Math.max(1, Math.round(len / 0.17));
    for (let i = 0; i <= n; i++) {
      const f = i / n;
      const x = x0 + (x1 - x0) * f,
        y = y0 + (y1 - y0) * f;
      const tall = 0.38 + hashXY(x, y, 71) * 0.08;
      const stake = look.stake2 && i % 2 ? look.stake2 : look.stake;
      out.push(cyl(x, y, 0.05, 0, tall, stake), cone(x, y, 0.055, tall, 0.08, look.tip));
    }
  } else {
    const { h, t, walk } = CURTAIN;
    // Into the land: across the run, on the side `inward` points.
    const side = o.inward ? Math.sign(-uy * o.inward[0] + ux * o.inward[1]) || 1 : 1;
    const ix = -uy * side,
      iy = ux * side;
    const along = Math.abs(ux) > 0.5;
    const ox = along ? 0 : t / 2,
      oy = along ? t / 2 : 0;
    const bx0 = Math.min(x0, x1) - ox,
      by0 = Math.min(y0, y1) - oy;
    const bw = Math.abs(x1 - x0) + 2 * ox,
      bd = Math.abs(y1 - y0) + 2 * oy;
    out.push(box(bx0, by0, bw, bd, 0, h, look.stone, { roof: true, top: shade(look.stone, 0.2) }));
    // The walkway: timber boards along the inner side, a little below the wall's top.
    const wx = along ? Math.min(x0, x1) : Math.min(x0, x1) + ix * (t / 2) + (ix < 0 ? -walk : 0),
      wy = along ? Math.min(y0, y1) + iy * (t / 2) + (iy < 0 ? -walk : 0) : Math.min(y0, y1);
    out.push(
      box(
        wx,
        wy,
        along ? Math.abs(x1 - x0) : walk,
        along ? walk : Math.abs(y1 - y0),
        h - 0.08,
        0.04,
        A.timber,
      ),
    );
    // Merlons along the outer edge.
    const n = Math.max(1, Math.floor(len / 0.2));
    for (let i = 0; i < n; i++) {
      const f = (i + 0.5) / n;
      out.push(
        box(
          x0 + (x1 - x0) * f - 0.05 - ix * (t / 2 - 0.05),
          y0 + (y1 - y0) * f - 0.05 - iy * (t / 2 - 0.05),
          0.1,
          0.1,
          h,
          0.09,
          look.stone,
        ),
      );
    }
    // A banner hung on the outer face every two tiles.
    for (let d = 1; d < len - 0.5; d += 2)
      out.push(
        box(
          x0 + ux * d - ix * (t / 2 + 0.006) - (along ? 0.07 : 0.006),
          y0 + uy * d - iy * (t / 2 + 0.006) - (along ? 0.006 : 0.07),
          along ? 0.14 : 0.012,
          along ? 0.012 : 0.14,
          h * 0.3,
          h * 0.6,
          flagC,
        ),
      );
    // Braziers on the walkway: an iron bowl on a stem, a fire in it at night.
    for (let d = CURTAIN.brazierEvery / 2; d < len - 0.4; d += CURTAIN.brazierEvery) {
      const bx = x0 + ux * d + ix * (t / 2 + walk / 2),
        by = y0 + uy * d + iy * (t / 2 + walk / 2);
      out.push(
        cyl(bx, by, 0.012, h - 0.04, 0.1, A.iron),
        cyl(bx, by, 0.045, h + 0.06, 0.035, A.iron),
      );
      if (o.night) out.push(flame(bx, by, h + 0.09, 0.035));
    }
    if (level >= 2) {
      const at: Array<[number, number]> = [];
      if (towers.start) at.push([x0, y0]);
      if (towers.end) at.push([x1, y1]);
      for (const [x, y] of at)
        out.push(...roundTower(x, y, 0.28, 1.2, look.stone, { roof: A.slate, flagC }));
    }
  }
  const m = 0.35;
  return {
    x: Math.min(x0, x1) - m,
    y: Math.min(y0, y1) - m,
    w: Math.abs(x1 - x0) + 2 * m,
    d: Math.abs(y1 - y0) + 2 * m,
    pieces: out,
  };
}

/**
 * The whole wall: a run along each of the world's own edges (`hedges`) and round each piece of
 * open land beside it, a tower at each corner once (never two on the same spot).
 */
export function wallUnits(
  hedges: ReadonlyArray<readonly [number, number, number, number]>,
  lands: readonly Bounds[],
  level: number,
  flagC: string,
  fence?: string,
  night = false,
): ArenaUnit[] {
  const look = palisadeOf(fence);
  const lvl = wallLevel(level, fence);
  const runs: Array<[number, number, number, number]> = hedges.map(([a, b, c, d]) => [a, b, c, d]);
  for (const l of lands)
    runs.push(
      [l.x, l.y, l.x + l.w, l.y],
      [l.x, l.y + l.h, l.x + l.w, l.y + l.h],
      [l.x, l.y, l.x, l.y + l.h],
      [l.x + l.w, l.y, l.x + l.w, l.y + l.h],
    );
  const seen = new Set<string>();
  const first = (x: number, y: number) => {
    const k = `${x},${y}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  };
  // Into the land from each run: towards the middle of all the wall goes round.
  const xs = runs.flatMap(([a, , c]) => [a, c]),
    ys = runs.flatMap(([, b, , d]) => [b, d]);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2,
    cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return runs.map(([x0, y0, x1, y1]) =>
    wallUnit(x0, y0, x1, y1, lvl, flagC, { start: first(x0, y0), end: first(x1, y1) }, look, {
      inward: [cx - (x0 + x1) / 2, cy - (y0 + y1) / 2],
      night,
    }),
  );
}

/* ---------- D420: the realm round the stronghold, for both renderers ---------- */

/** A cottage of the realm on its lot: a thatched house, a garden plot, a woodpile. */
export function cottageUnit(x: number, y: number, w: number, h: number): ArenaUnit {
  const r = hashXY(x, y, 81);
  const hx = x + 0.8 + r * (w - 3),
    hy = y + 0.8 + hashXY(x, y, 82) * (h - 3);
  const wall = r < 0.5 ? A.white : '#D9C9A8';
  const pieces: ArenaPiece[] = [
    ...hut(hx, hy, 1.1, 0.7, 0.36, wall, r < 0.3 ? A.tile : A.thatch, { rows: 1 }),
    box(hx + 0.1, hy + 0.12, 0.12, 0.12, 0.3, 0.42, A.stoneDark),
    ...logPile(hx + 1.25, hy + 0.2, 3),
  ];
  return { x: hx, y: hy, w: 1.6, d: 0.9, pieces };
}

/**
 * The stronghold's gate where the road comes in at (x, y), the road running from (px, py):
 * posts and a lintel behind the palisade, a gatehouse of two towers once the curtain wall
 * stands (D437), roofed at form 2; its leaves in the gate decoration's colour, standing open.
 */
export function gateUnit(
  g: { x: number; y: number; px: number; py: number },
  level: number,
  gateC: string,
  flagC: string,
  fence?: string,
): ArenaUnit {
  const look = palisadeOf(fence);
  level = wallLevel(level, fence);
  const ax = g.x - g.px,
    ay = g.y - g.py;
  const l = Math.hypot(ax, ay) || 1;
  const sx = -ay / l,
    sy = ax / l;
  const half = 0.45;
  const L: [number, number] = [g.x + sx * half, g.y + sy * half],
    R: [number, number] = [g.x - sx * half, g.y - sy * half];
  const out: ArenaPiece[] = [];
  if (level <= 0) {
    const h = 0.7;
    for (const [x, y] of [L, R])
      out.push(cyl(x, y, 0.06, 0, h, look.stake), cone(x, y, 0.065, h, 0.08, look.tip));
    out.push(rod([L[0], L[1], h - 0.06], [R[0], R[1], h - 0.06], 0.07, A.log));
    out.push(...banner(L[0], L[1], 0.3, flagC, h + 0.04));
    out.push(...torch(R[0] + (ax / l) * 0.15, R[1] + (ay / l) * 0.15, 0.4));
  } else {
    const H = level === 1 ? 0.95 : 1.25;
    for (const [x, y] of [L, R])
      out.push(
        ...roundTower(x, y, 0.22, H, look.stone, level >= 2 ? { roof: A.slate, flagC } : { flagC }),
      );
    const t = 0.22;
    out.push(rod([L[0], L[1], H * 0.7], [R[0], R[1], H * 0.7], t, look.stone));
    for (const [x, y] of [L, R])
      out.push(flame(x + (ax / l) * 0.25, y + (ay / l) * 0.25, 0.42, 0.04));
  }
  // The leaves, swung open against the posts.
  for (const [x, y] of [L, R]) {
    const ix = x + (g.x - x) * 0.18,
      iy = y + (g.y - y) * 0.18;
    out.push(rod([ix, iy, 0.02], [ix - (ax / l) * 0.32, iy - (ay / l) * 0.32, 0.02], 0.04, gateC));
    out.push(rod([ix, iy, 0.4], [ix - (ax / l) * 0.32, iy - (ay / l) * 0.32, 0.4], 0.04, gateC));
    out.push(
      box(
        Math.min(ix, ix - (ax / l) * 0.32) - 0.01,
        Math.min(iy, iy - (ay / l) * 0.32) - 0.01,
        Math.abs(ax / l) * 0.32 + 0.02,
        Math.abs(ay / l) * 0.32 + 0.02,
        0.02,
        0.4,
        gateC,
      ),
    );
  }
  return { x: g.x - 0.6, y: g.y - 0.6, w: 1.2, d: 1.2, pieces: out };
}

/* ---------- heights ---------- */

/** The top of one piece, over the ground. */
export function pieceTop(q: ArenaPiece): number {
  switch (q.s) {
    case 'cone':
    case 'gable':
    case 'flame':
    case 'crystal':
      return q.z + q.h;
    case 'flag':
      return q.z;
    default:
      return basePieceTop(q);
  }
}

/** The top of a unit's tallest piece. */
export function unitTop(u: ArenaUnit): number {
  let t = 0;
  for (const q of u.pieces) t = Math.max(t, pieceTop(q));
  return t;
}
